﻿import * as React from 'react';
import styles from './style.module.scss';
import SvgEditService from '../../../services/svg-edit-service';
import ZenDataService from '../../../services/zen-data-service';
import PanelsService from '../../../services/panels-service';
import ColoursService from '../../../services/colours-service';
import { ModalDialog } from '../modal-dialog';
import { DEFAULT_PANEL_GENERATOR_VERSION } from '../../../common/constants';
import { text2xml, xml2text } from '../../../svgcanvas/utilities';
import { NS } from '../../../common/namespaces';
import { Resolver_ImportToLayer } from './resolvers';
import { BaseResolver } from './resolvers/base-resolver';
import { Resolver_Colours } from './resolvers/colours-resolver';
import { Resolver_SpotColours } from './resolvers/spot-colours-resolver';
import { Resolver_StyleToAttribute } from './resolvers/style-to-attrib-resolver';

type ImportReturnType = { svg: string, layerName: string } | false;
type ImportPromiseResolver = (result: ImportReturnType | PromiseLike<ImportReturnType>) => void;

interface State {
    destinationLayerName: string;
}

export class ImportSvgResolverDialog extends React.Component<{}, State> {
    state = {
        destinationLayerName: "",
    };

    private svgEditService = SvgEditService.Get(this);
    private zenDataService = ZenDataService.Get(this);
    private panelService = PanelsService.Get(this);
    private colourService = ColoursService.Get(this);

    private dialog: React.RefObject<ModalDialog> = React.createRef();

    private svgContainer: HTMLTemplateElement;
    private resolvers: BaseResolver[] = [];
    private openMode: "Open" | "Import";
    private promiseResolver: ImportPromiseResolver;

    public componentWillUnmount() {
        this.svgEditService.UnregisterComponent(this);
        this.zenDataService.UnregisterComponent(this);
        this.panelService.UnregisterComponent(this);
        this.colourService.UnregisterComponent(this);
    }

    public openDialog = (svgFileContents: string | ArrayBuffer, mode: "Open" | "Import", importInitialLayerName?: string) => {
        this.openMode = mode;

        if (typeof svgFileContents !== "string") {
            const decoder = new TextDecoder("utf-8");
            svgFileContents = decoder.decode(new Uint8Array(svgFileContents));
        }

        const xmlDoc = text2xml(svgFileContents);
        if (xmlDoc.firstElementChild && xmlDoc.firstElementChild.namespaceURI !== NS.SVG) {
            return false;
        }

        // Need to adopt the root node to make it accessible to the DOM
        const rootNode = this.svgEditService.canvas.getDOMDocument().adoptNode(xmlDoc.documentElement);

        // Store the imported SVG it in our invisible template element
        this.svgContainer = document.createElement("template");
        this.svgContainer.appendChild(rootNode);

        // Perform some cleanup
        const svgRoot = this.svgContainer.firstElementChild as SVGSVGElement;
        if (!svgRoot.getAttribute("zaa:dpi")) { // Force 96 DPI as default
            svgRoot.setAttribute("zaa:dpi", "96");
        }
        if (!svgRoot.getAttribute("zaa:version")) {
            svgRoot.setAttribute("zaa:version", DEFAULT_PANEL_GENERATOR_VERSION); // Newly opened panels should use the newer panel generator
        }

        // Setup our resolver stack
        const possibleResolvers = [
            new Resolver_ImportToLayer(mode, importInitialLayerName, v => this.setState({ destinationLayerName: v })),
            new Resolver_StyleToAttribute(),
            new Resolver_Colours(svgRoot),
            new Resolver_SpotColours(svgRoot),
        ];
        this.resolvers = possibleResolvers.filter(r => r.needResolution());

        // And finalize
        this.setState({
            destinationLayerName: importInitialLayerName
        });
        // If a previous resolution is somehow still pending, treat it as cancelled before starting a new one.
        this.settleImport(false);

        this.dialog.current.showModal();

        return new Promise<ImportReturnType>((resolve) => {
            this.promiseResolver = resolve;

            const visibleResolvers = this.resolvers.filter(r => r.component);

            if (visibleResolvers.length == 0) {
                 // Nothing to resolve or nothing with UI? Fast track.
                this.finalizeImport();
            }
        });
    }

    private finalizeImport() {
        this.resolvers.sort((a, b) => {
            return a.resolveOrder - b.resolveOrder;
        });

        for (const resolver of this.resolvers) {
            resolver.resolveSvg(this.svgContainer);
        }

        let resultSVG: string;
        if (this.openMode === "Open") {
            // Open always results in a single SVG first child. This string needs to be a valid XML string.
            resultSVG = `<?xml version="1.0" encoding="utf-8"?>${xml2text(this.svgContainer.firstElementChild)}`;
        } else {
            // Import SVG unpacking could result in many children. Concat them all.
            resultSVG = "";
            for (const child of this.svgContainer.children) {
                resultSVG += xml2text(child) + "\n";
            }
        }

        this.settleImport({
            svg: resultSVG,
            layerName: this.state.destinationLayerName,
        });
        this.dialog.current.close();
    }

    private cancelImport() {
        this.settleImport(false);
        this.dialog.current.close();
    }

    /**
     * Resolves the pending openDialog() promise exactly once. Any later calls (for example the dialog's
     * native close event firing after a successful import) are ignored.
     */
    private settleImport(result: ImportReturnType) {
        const resolver = this.promiseResolver;
        this.promiseResolver = null;
        resolver?.(result);
    }

    public render() {
        const visibleResolvers = this.resolvers.filter(r => r.component);

        return (
            <ModalDialog ref={this.dialog} title={`${this.openMode} SVG File`} id="pd-importsvgdialog" className={styles.importDialog} aria-label={`${this.openMode} SVG File Dialog`}
                onClose={() => this.settleImport(false)}>
                <div className={styles.resolverWrapper}>
                    {visibleResolvers.map(resolver => (
                        <section key={resolver.id} className={styles.resolverSection}>
                            {resolver.component}
                        </section>
                    ))}
                    {visibleResolvers.length == 0 && (
                        <section className={styles.resolverSection}>
                            There is nothing to resolve with this SVG. You may continue.
                        </section>
                    )}
                </div>

                <section className={styles.confirmButtons}>
                    <button type="button" className="btn btn-primary" onClick={() => this.finalizeImport()}>{this.openMode === "Open" ? "Open SVG" : "Finalize Import"}</button>
                    <button type="button" className="btn btn-secondary" onClick={() => this.cancelImport()}>Cancel</button>
                </section>
            </ModalDialog>
        );
    }
}

